// Presentation validation only; clinical and coding decisions stay on the reviewed server path.
export const RESOURCE_CONTRACT = 'woundos-consultation-resources-v1';
const obj = v => v && typeof v === 'object' && !Array.isArray(v);
const id = v => typeof v === 'string' && /^[A-Za-z][A-Za-z0-9_-]{0,47}$/.test(v);
const text = (v, n = 6000) => typeof v === 'string' && v.trim() && v.length <= n * 2 && Array.from(v).length <= n;
const hash = v => typeof v === 'string' && /^[0-9a-f]{64}$/.test(v);
const list = (v, n) => Array.isArray(v) && v.length <= n && v.every(obj);
const ids = v => v.every(a => id(a.id)) && new Set(v.map(a => a.id)).size === v.length;
const refs = (v, allowed) => Array.isArray(v) && v.length <= 256
  && new Set(v).size === v.length && v.every(k => id(k) && allowed.has(k));
const strings = v => Array.isArray(v) && v.length <= 64 && v.every(a => text(a));
const same = (a, b) => {
  if (Array.isArray(a)) return Array.isArray(b) && a.length === b.length && a.every((v, i) => same(v, b[i]));
  if (obj(a)) return obj(b) && Object.keys(a).length === Object.keys(b).length
    && Object.keys(a).every(k => Object.hasOwn(b, k) && same(a[k], b[k]));
  return a === b;
};
const exact = (v, fields) => obj(v) && Object.keys(v).length === fields.length && fields.every(k => Object.hasOwn(v, k));
const projection = (v, original) => Object.keys(original).every(k => Object.hasOwn(v, k) && same(v[k], original[k]));
const phases = new Set(['assessment', 'cleanse', 'analgesia', 'debridement', 'hemostasis',
  'remeasure', 'contact_layer', 'primary', 'secondary', 'periwound', 'securement',
  'compression', 'offloading', 'support_surface', 'npwt', 'reconstruction', 'education']);
const iso = v => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)
  && Number.isFinite(Date.parse(v)) && new Date(v).toISOString().slice(0, 10) === v;
const code = v => typeof v === 'string' && /^[A-Z][A-Z0-9]{2}(?:\.[A-Z0-9]{1,4})?$/.test(v);

export function normalizeResources(p, interaction) {
  try { return normalizeResourceContract(p, interaction); } catch { return null; }
}

function normalizeResourceContract(p, interaction) {
  const sections = ['proposed_orders', 'product_recommendations', 'billing_codes',
    'incomplete_code_families', 'coverage_review', 'resource_gaps'];
  if (!Object.hasOwn(p, 'resource_contract_version')) {
    return sections.every(k => !Object.hasOwn(p, k) || (Array.isArray(p[k]) && !p[k].length))
      && !p.citations.some(v => v.role === 'code_definition' || Object.hasOwn(v, 'resource_kind')) ? undefined : null;
  }
  if (p.resource_contract_version !== RESOURCE_CONTRACT || sections.some(k => !list(p[k], 128))
      || p.resource_review_scope !== 'separate_model_review_not_human_clinical_or_coding_approval') return null;
  const plan = p.reviewed_resource_plan;
  if (!exact(plan, ['orders', 'products', 'codes', 'coverage', 'gaps'])
      || ['orders', 'products', 'codes', 'coverage'].some(k => !list(plan[k], 64) || !ids(plan[k]))) return null;
  if (!list(plan.gaps, 4) || !same(plan.gaps, p.resource_gaps)) return null;
  const gapSections = plan.gaps.map(v => v.section);
  if (new Set(gapSections).size !== gapSections.length || plan.gaps.some(v =>
    !exact(v, ['section', 'status', 'reason']) || !['orders', 'products', 'codes', 'coverage'].includes(v.section)
    || !['not_applicable', 'missing_patient_data', 'source_unavailable'].includes(v.status) || !text(v.reason))) return null;
  if (['orders', 'products', 'codes', 'coverage'].some(k => Boolean(plan[k].length) === gapSections.includes(k))) return null;
  const rawFacts = p.clinical_interaction?.canonical_fact_ledger?.records;
  if (!list(rawFacts, 256) || !ids(rawFacts) || !list(p.reviewed_assessments, 24) || !ids(p.reviewed_assessments)) return null;
  const factIds = new Set(rawFacts.map(v => v.id));
  const facts = new Map(rawFacts.map(v => [v.id, v]));
  const actions = new Map(p.reviewed_actions.map(v => [v.id, v]));
  const sourceMap = new Map(p.citations.map(v => [v.id, v]));
  const decisions = new Set(p.open_decisions.map(v => v.id));
  const assessments = new Map(p.reviewed_assessments.map(v => [v.id, v]));
  if (p.reviewed_assessments.some(v => !text(v.text) || !['working', 'source_confirmed', 'alternative'].includes(v.certainty)
      || !refs(v.fact_ids, factIds) || !v.fact_ids.length)) return null;
  const pins = new Set();
  for (const s of p.citations.filter(v => Object.hasOwn(v, 'resource_kind') || v.role === 'code_definition')) {
    if (!['product', 'policy', 'code'].includes(s.resource_kind) || !hash(s.resource_catalog_sha256)
        || s.role !== { product: 'device_ifu', policy: 'payer', code: 'code_definition' }[s.resource_kind]) return null;
    pins.add(s.resource_catalog_sha256);
  }
  if (pins.size > 1) return null;
  const resourceSource = (sourceId, kind) => sourceMap.get(sourceId)?.resource_kind === kind;
  const supported = action => action && refs(action.fact_ids, factIds) && action.fact_ids.length
    && action.evidence_ids.some(k => ['clinical_primary', 'clinical_reference'].includes(sourceMap.get(k)?.role));
  const orderKeys = ['id', 'action_id', 'phase', 'frequency', 'change_trigger', 'precautions', 'responsible_role'];
  const publicOrderKeys = [...orderKeys, 'wound_id', 'instruction', 'rationale', 'fact_ids', 'evidence_ids', 'status', 'product_ids'];
  if (p.proposed_orders.length !== plan.orders.length || !ids(p.proposed_orders)) return null;
  for (const [i, v] of p.proposed_orders.entries()) {
    const b = plan.orders[i]; const action = actions.get(v.action_id);
    if (!exact(b, orderKeys) || !exact(v, publicOrderKeys) || !projection(v, b) || !phases.has(v.phase)
        || !['frequency', 'change_trigger', 'precautions', 'responsible_role'].every(k => text(v[k]))
        || !supported(action) || !['treatment', 'diagnostic', 'immediate_safety'].includes(action.kind)
        || (action.kind === 'treatment' && !interaction.treatment_plan_available)
        || v.status !== 'proposed_not_performed' || v.instruction !== action.text || v.rationale !== action.rationale
        || !same(v.fact_ids, action.fact_ids) || !same(v.evidence_ids, action.evidence_ids)
        || !id(v.wound_id) || v.fact_ids.some(k => facts.get(k).wound_id !== v.wound_id)
        || !same(v.product_ids, plan.products.filter(a => a.action_ids.includes(action.id)).map(a => a.id))) return null;
  }
  const productKeys = ['id', 'resource_id', 'source_id', 'action_ids', 'rationale'];
  const definitionKeys = ['id', 'name', 'variant', 'generic_class', 'indications', 'preparation', 'application',
    'change_instructions', 'contraindications', 'warnings', 'compatibility',
    'materials', 'active_ingredients', 'sizing', 'secondary_layers'];
  if (p.product_recommendations.length !== plan.products.length || !ids(p.product_recommendations)) return null;
  for (const [i, v] of p.product_recommendations.entries()) {
    const b = plan.products[i]; const def = v.definition;
    if (!exact(b, productKeys) || !exact(v, [...productKeys, 'definition', 'fields_not_documented',
      'selection_status', 'instructions_scope', 'catalog_sha256']) || !projection(v, b)
      || !exact(def, definitionKeys) || !id(v.resource_id) || def.id !== v.resource_id || !text(v.rationale)
      || !resourceSource(v.source_id, 'product') || v.catalog_sha256 !== sourceMap.get(v.source_id).resource_catalog_sha256
      || !interaction.treatment_plan_available || !refs(v.action_ids, new Set(actions.keys())) || !v.action_ids.length) return null;
    if (!['name', 'variant', 'generic_class'].every(k => text(def[k], 300))
      || !definitionKeys.slice(4).every(k => strings(def[k])) || !def.application.length
      || v.selection_status !== 'proposed_for_clinician_review'
      || v.instructions_scope !== 'manufacturer_excerpts_not_independent_efficacy_evidence'
      || !same(v.fields_not_documented, ['preparation', 'change_instructions', 'contraindications', 'warnings', 'compatibility',
        'materials', 'active_ingredients', 'sizing', 'secondary_layers'].filter(k => !def[k].length))
      || v.action_ids.some(k => actions.get(k).kind !== 'treatment' || !supported(actions.get(k))
        || !plan.orders.some(o => o.action_id === k))) return null;
  }
  const dateMatches = (factId, value) => factId === null ? value === null : factIds.has(factId)
    && facts.get(factId).value === value && iso(value) && facts.get(factId).polarity === 'PRESENT'
    && facts.get(factId).temporal_status === 'current';
  const codeKeys = ['id', 'code', 'kind', 'assessment_id', 'rationale', 'uncertainty_ids', 'date_fact_id', 'source_id'];
  const codeExtra = ['descriptor', 'code_system', 'complete_definition', 'definition_release', 'effective_from',
    'effective_to', 'instructions', 'diagnosis_certainty', 'supporting_fact_ids', 'encounter_date',
    'encounter_date_status', 'claim_submission_authorized', 'status'];
  const presentedCodes = [...p.billing_codes, ...p.incomplete_code_families];
  if (presentedCodes.length !== plan.codes.length || !ids(presentedCodes)) return null;
  for (const b of plan.codes) {
    const v = presentedCodes.find(a => a.id === b.id); const a = assessments.get(b.assessment_id);
    if (!exact(b, codeKeys) || !exact(v, [...codeKeys, ...codeExtra]) || !projection(v, b)
      || !code(v.code) || !text(v.descriptor, 1000) || v.code_system !== 'ICD-10-CM' || !text(v.rationale)
      || !text(v.definition_release, 300) || !iso(v.effective_from) || !iso(v.effective_to)
      || v.effective_from > v.effective_to || !resourceSource(v.source_id, 'code')
      || !a || a.certainty === 'alternative' || v.diagnosis_certainty !== a.certainty
      || !same(v.supporting_fact_ids, a.fact_ids) || !refs(v.uncertainty_ids, decisions)
      || !dateMatches(v.date_fact_id, v.encounter_date) || v.claim_submission_authorized !== false
      || !list(v.instructions, 128) || v.instructions.some(n => !exact(n, ['kind', 'origin', 'text'])
        || !text(n.kind, 300) || !text(n.origin, 300) || !text(n.text))) return null;
    if (v.encounter_date !== null && (v.encounter_date < v.effective_from || v.encounter_date > v.effective_to)) return null;
    if (v.encounter_date_status !== (v.encounter_date ? 'within_definition_release' : 'unconfirmed')) return null;
    const complete = v.kind === 'candidate';
    if (!['candidate', 'incomplete_family'].includes(v.kind) || v.complete_definition !== complete
      || v.status !== (complete ? 'candidate_for_clinician_and_coder_review' : 'incomplete_family_not_for_billing')
      || !(complete ? p.billing_codes : p.incomplete_code_families).includes(v)
      || (!complete && !v.uncertainty_ids.length)) return null;
  }
  const coverageKeys = ['id', 'resource_id', 'source_id', 'action_ids', 'applicability',
    'applicability_fact_ids', 'date_fact_id', 'rationale', 'criteria'];
  const coverageExtra = ['policy_id', 'service', 'jurisdiction', 'effective_from', 'effective_to',
    'scope', 'coverage_guaranteed', 'clinical_indication_established_by_policy'];
  const criterionKeys = ['criterion_id', 'status', 'fact_ids', 'uncertainty_ids', 'rationale'];
  if (p.coverage_review.length !== plan.coverage.length || !ids(p.coverage_review)) return null;
  for (const [i, v] of p.coverage_review.entries()) {
    const b = plan.coverage[i];
    if (!exact(b, coverageKeys) || !exact(v, [...coverageKeys, ...coverageExtra])
      || !projection(v, Object.fromEntries(Object.entries(b).filter(([k]) => k !== 'criteria')))
      || !resourceSource(v.source_id, 'policy') || !text(v.rationale)
      || !['policy_id', 'service', 'jurisdiction'].every(k => text(v[k], 300))
      || !iso(v.effective_from) || !iso(v.effective_to) || v.effective_from > v.effective_to
      || !['matched', 'unconfirmed'].includes(v.applicability)
      || !refs(v.applicability_fact_ids, factIds) || !refs(v.action_ids, new Set(actions.keys()))
      || !v.action_ids.length || v.action_ids.some(k => !supported(actions.get(k)))
      || v.coverage_guaranteed !== false || v.clinical_indication_established_by_policy !== false
      || v.scope !== 'selected_criteria_documentation_worksheet_not_coverage_determination'
      || !list(v.criteria, 64) || !list(b.criteria, 64) || v.criteria.length !== b.criteria.length) return null;
    const dateValue = v.date_fact_id === null ? null : facts.get(v.date_fact_id)?.value;
    if (!dateMatches(v.date_fact_id, dateValue)) return null;
    if (v.applicability === 'matched' && (!v.applicability_fact_ids.length || !dateValue
      || dateValue < v.effective_from || dateValue > v.effective_to)) return null;
    if (new Set(v.criteria.map(c => c.criterion_id)).size !== v.criteria.length) return null;
    for (const [j, c] of v.criteria.entries()) {
      if (!exact(b.criteria[j], criterionKeys) || !exact(c, [...criterionKeys, 'criterion_text'])
        || !projection(c, b.criteria[j]) || !id(c.criterion_id) || !text(c.criterion_text) || !text(c.rationale)
        || !['met', 'not_met', 'unknown', 'not_applicable'].includes(c.status)
        || !refs(c.fact_ids, factIds) || !refs(c.uncertainty_ids, decisions)
        || (c.status === 'unknown' ? !c.uncertainty_ids.length : !c.fact_ids.length)) return null;
    }
  }
  return structuredClone({ orders: p.proposed_orders, products: p.product_recommendations,
    codes: p.billing_codes, families: p.incomplete_code_families, coverage: p.coverage_review, gaps: p.resource_gaps });
}
